import React from "react";
import type { LucideIcon } from "lucide-react";
import {
    Carousel,
    CarouselContent,
    CarouselNext,
    CarouselPrevious,
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";

/**
 * Shared mobile ("lg:hidden") presentation for the home discovery rails — recommended
 * publishers/series/groups and the continue-reading resume row. Each rail reads like a
 * discussion card: a rounded card surface (styled via the `data-home-inline-discovery`
 * rules in 01-base.css) with a padded header carrying a lucide icon, and the carousel as
 * the card's "media" region with horizontally centered slides.
 *
 * Callers pass already-prepared `<CarouselItem>` cards so each rail keeps its own card
 * content; this component owns the surface, header, and carousel chrome so the rails stay
 * visually consistent.
 */
export function HomeMobileDiscoveryRailSection(props: {
    icon: LucideIcon;
    title: string;
    subtitle: string;
    cards: React.ReactNode[];
    /**
     * `HOME-FEED-18` — accessible names for the two carousel arrows. **Required, not optional.**
     *
     * `components/ui/carousel.tsx` gives every arrow a hardcoded English `sr-only` name ("Previous
     * slide" / "Next slide"). That is not a missing name, it is an untranslated one, and it is
     * invisible to BOTH localization build guards: key parity and the verbatim check read catalogs,
     * and a literal inside a shared primitive never reaches one. So nothing fails, in all 25 locales,
     * forever. An `aria-label` on the element overrides that `sr-only` text.
     *
     * Required so a future call site cannot silently regress to the literal — the compiler is the only
     * guard available here, precisely because the guards that would normally catch this are blind to
     * it. The sibling `DiscussionCardMediaCarousel` already passes localized labels this way.
     */
    previousLabel: string;
    nextLabel: string;
    loading?: boolean;
    loadingLabel?: string;
    errorNode?: React.ReactNode;
    sectionPlaywrightId?: string;
    carouselPlaywrightId?: string;
    className?: string;
}) {
    const {
        icon: Icon,
        title,
        subtitle,
        cards,
        previousLabel,
        nextLabel,
        loading = false,
        loadingLabel,
        errorNode,
        sectionPlaywrightId,
        carouselPlaywrightId,
        className,
    } = props;

    return (
        <section
            className={cn("lg:hidden", className)}
            data-playwright-id={sectionPlaywrightId}
            data-home-inline-discovery="module"
        >
            <div className="flex items-start gap-3" data-home-inline-discovery-header="true">
                <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full border border-border/50 bg-muted/70 text-text-main shadow-[inset_0_1px_0_rgba(255,255,255,0.22)] dark:shadow-none">
                    <Icon className="size-4.5" />
                </div>
                <div className="min-w-0">
                    <h2 className="text-sm font-semibold text-text-main">{title}</h2>
                    <p className="text-xs text-neutral-text">{subtitle}</p>
                </div>
            </div>

            <div data-home-inline-discovery-track="true">
                {loading ? (
                    <div className="px-2 py-2 text-sm text-neutral-text">{loadingLabel}</div>
                ) : errorNode ? (
                    <div className="px-2">{errorNode}</div>
                ) : (
                    <Carousel
                        opts={{
                            align: "center",
                            containScroll: "trimSnaps",
                            dragFree: false,
                            loop: false,
                        }}
                        // `HOME-FEED-17`. `px-9` reserves 36px each side for the two 32px arrows. On a
                        // phone that is a 72px gutter out of a ~348px module, so the recommendation
                        // rendered 275px wide against a 348px feed card — 21% narrower than the posts it
                        // competes with, which wrapped a 26-character group name onto two lines.
                        //
                        // On a COARSE pointer the gutter drops to `px-4`, which lines the slide up with the
                        // module header's own 1.1rem inset rather than with an arrow, and the arrows are
                        // hidden below (they duplicate the swipe gesture the surface already has).
                        //
                        // This padding is on the carousel ROOT, i.e. OUTSIDE embla's `overflow-hidden`
                        // viewport — it changes the viewport width, which embla re-measures itself, and the
                        // slides are `basis-full` so `align: "center"` is a no-op for them. That is why this
                        // is a class swap and not the layout surgery the register predicted; the padding its
                        // constraint describes is the TRACK's, and the track carries `ml-0` and no inline
                        // padding at all.
                        className="w-full px-9 [@media(pointer:coarse)]:px-4"
                        data-playwright-id={carouselPlaywrightId}
                        // The carousel is a role="region" landmark. Unnamed, the home page hands a
                        // screen-reader user several identical "carousel" landmarks and the rails become
                        // indistinguishable in the landmark list — so a shared constant would be no
                        // better than nothing. The rail's own heading is the right name, and it is
                        // already localized by the caller, so no new catalog key is involved.
                        aria-label={title}
                    >
                        {/* `pt-1 pb-6`: `HOME-FEED-7`. This className lands on the flex TRACK inside embla's
                            `overflow-hidden` viewport, which had zero vertical slack — so the slides' sanctioned
                            lift-on-hover shadow (`0 10px 24px` at rest, reaching 22px below and needing 2px above
                            for the lift) was cut flat on both edges. Vertical only: the track's INLINE padding is
                            what `align: "center"` + `containScroll: "trimSnaps"` measure their snap points from,
                            so widening it sideways would move the slides rather than free the shadow.
                            Guard: `HomeDiscoveryRail.spec.tsx`, "HOME-FEED-7". */}
                        <CarouselContent className="ml-0 items-stretch pt-1 pb-6">
                            {cards}
                        </CarouselContent>
                        {/* `HOME-FEED-17`. Hidden on a coarse pointer: the surface is already swipeable and
                            these two controls duplicate that gesture, while costing the slide 21% of its
                            width to sit in. Nothing is stranded by their absence — embla v8's `SlideFocus`
                            scrolls an offscreen slide into view when a link inside it takes focus, and the
                            carousel region handles ArrowLeft/ArrowRight itself. They stay on a fine pointer
                            (a narrow desktop window is still below `lg`), which is also where
                            `HOME-FEED-18`'s localized names on them stay live. */}
                        <CarouselPrevious
                            aria-label={previousLabel}
                            className="left-0 size-8 rounded-full border-border bg-muted/90 text-text-main shadow-none [@media(pointer:coarse)]:hidden"
                            data-home-inline-discovery-arrow="true"
                        />
                        <CarouselNext
                            aria-label={nextLabel}
                            className="right-0 size-8 rounded-full border-border bg-muted/90 text-text-main shadow-none [@media(pointer:coarse)]:hidden"
                            data-home-inline-discovery-arrow="true"
                        />
                    </Carousel>
                )}
            </div>
        </section>
    );
}
